import * as React from 'react';
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { PageTitleContext } from './__root';
import { AvailabilityImportWizard } from '@ooh/screens';

// ─── Search params ────────────────────────────────────────────────────────────

const AvailabilityImportSearchSchema = z.object({
  importJobId: z.coerce.number().int().positive().optional().catch(undefined),
  phase: z.enum(['upload', 'header-row', 'preview', 'result']).optional().catch(undefined),
});

export type AvailabilityImportSearch = z.infer<typeof AvailabilityImportSearchSchema>;

// ─── Route ────────────────────────────────────────────────────────────────────
// Intentionally no `beforeLoad` redirect: per availability-import-ui's "Caller
// without permission does not see the entry point" scenario, direct navigation
// SHALL render an access-denied state (not a silent redirect) — mirrors
// permission-matrix-page.tsx's in-component gating, not media/import.tsx's
// beforeLoad-redirect pattern.

export const Route = createFileRoute('/availability-import')({
  validateSearch: AvailabilityImportSearchSchema,
  component: AvailabilityImportRoute,
});

export function AvailabilityImportRoute() {
  const { setPageTitle } = React.useContext(PageTitleContext);
  const { t } = useTranslation('availabilityImport');
  const navigate = useNavigate();
  const search = useSearch({ from: '/availability-import' });

  React.useEffect(() => {
    setPageTitle(t('pageTitle'));
  }, [setPageTitle, t]);

  const phase = search.phase ?? 'upload';

  function handleUploadNext(jobId: number) {
    void navigate({
      to: '/availability-import',
      search: { phase: 'header-row', importJobId: jobId },
    });
  }

  function handleHeaderRowNext() {
    void navigate({
      to: '/availability-import',
      search: { phase: 'preview', importJobId: search.importJobId },
    });
  }

  function handleConfirmed() {
    void navigate({
      to: '/availability-import',
      search: { phase: 'result', importJobId: search.importJobId },
    });
  }

  function handleBackToUpload() {
    void navigate({ to: '/availability-import', search: { phase: 'upload' } });
  }

  function handleFinish() {
    void navigate({ to: '/media-units' });
  }

  return (
    <AvailabilityImportWizard
      phase={phase}
      jobId={search.importJobId ?? null}
      onUploadNext={handleUploadNext}
      onHeaderRowNext={handleHeaderRowNext}
      onConfirmed={handleConfirmed}
      onBackToUpload={handleBackToUpload}
      onFinish={handleFinish}
    />
  );
}
